import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material';
import {
  Logout,
  Notifications,
  Assignment,
  FlightTakeoff,
  Campaign,
  AttachMoney,
  DateRange,
  HomeWork,
  School,
  Description,
  Menu as MenuIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getRequestStats, getAllRequests } from "../services/dataServiceWrapper";
import { checkIfUserIsApprover } from '../services/graphService';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, cleanupOldNotifications } from '../services/notificationService';

const formTypes = [
  {
    id: 'leave',
    title: 'Leave Request',
    description: 'Request time off work',
    icon: <DateRange />,
    color: '#1976d2',
    bgColor: '#e3f2fd',
  },
  {
    id: 'travel',
    title: 'Travel Request',
    description: 'Submit travel arrangements',
    icon: <FlightTakeoff />,
    color: '#388e3c',
    bgColor: '#e8f5e9',
  },
  {
    id: 'marketing',
    title: 'Marketing Request',
    description: 'Request marketing materials',
    icon: <Campaign />,
    color: '#7b1fa2',
    bgColor: '#f3e5f5',
  },
  {
    id: 'reimbursement',
    title: 'Reimbursement Request',
    description: 'Claim expense reimbursements',
    icon: <AttachMoney />,
    color: '#d32f2f',
    bgColor: '#ffebee',
  },
  {
    id: 'extensions',
    title: 'Extensions Request',
    description: 'Request deadline extensions',
    icon: <Assignment />,
    color: '#f57c00',
    bgColor: '#fff3e0',
  },
  {
    id: 'wfh',
    title: 'WFH Checklist',
    description: 'Work from home requirements',
    icon: <HomeWork />,
    color: '#0288d1',
    bgColor: '#e1f5fe',
  },
  // {
  //   id: 'academic',
  //   title: 'Academic Changes',
  //   description: 'Submit academic modifications',
  //   icon: <School />,
  //   color: '#5e35b1',
  //   bgColor: '#ede7f6',
  // },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    description: 'View reports and analytics',
    icon: <Description />,
    color: '#00695c',
    bgColor: '#e0f2f1',
  },
];

const Dashboard = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    inProgress: 0,
    total: 0,
  });
  const [isApprover, setIsApprover] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
    
    // Load stats
    const loadStats = async () => {
      const requestStats = await getRequestStats(userEmail);
      setStats(requestStats);
    };
    loadStats();
    
    // Check if user is an approver
    const checkApproverStatus = async () => {
      console.log('🎯 Dashboard: Checking approver status for:', userEmail);
      const approverStatus = await checkIfUserIsApprover(userEmail, instance, account);
      console.log('🎯 Dashboard: Approver status:', approverStatus);
      setIsApprover(approverStatus);
      
      if (approverStatus) {
        // Get all pending requests where user is the approver
        const allRequests = await getAllRequests();
        console.log('📋 All requests:', allRequests);
        console.log('📋 Total requests count:', allRequests.length);
        
        // Also check localStorage directly
        const directData = JSON.parse(localStorage.getItem('acom_requests') || '[]');
        console.log('📋 Direct localStorage count:', directData.length);
        const academicInDirect = directData.filter(r => r.type === 'academic');
        console.log('📋 Academic requests in localStorage:', academicInDirect);
        
        // Add a unique identifier to track which localStorage we're looking at
        const storageId = localStorage.getItem('acom_storage_id');
        if (!storageId) {
          const newId = 'storage-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('acom_storage_id', newId);
          console.log('🆔 Created new storage ID:', newId);
        } else {
          console.log('🆔 Using existing storage ID:', storageId);
        }
        
        const pendingForApproval = allRequests.filter(req => {
          console.log(`Checking request: ${req.id}, type: ${req.type}, approver: ${req.approver}, approvers: ${JSON.stringify(req.approvers)}, status: ${req.status}`);
          
          // Special logging for academic requests
          if (req.type === 'academic') {
            console.log('🎓 Academic request found:', {
              id: req.id,
              title: req.title,
              status: req.status,
              approvers: req.approvers,
              userEmail: userEmail,
              hasApproversArray: Array.isArray(req.approvers),
              isPending: req.status === 'pending'
            });
          }
          
          // Check single approver (extensions)
          if (req.approver?.toLowerCase() === userEmail.toLowerCase() && req.status === 'pending') {
            console.log('✅ Matched as single approver');
            return true;
          }
          
          // Check multiple approvers (academic changes)
          if (req.approvers && Array.isArray(req.approvers) && req.status === 'pending') {
            const isApprover = req.approvers.some(approverEmail => 
              approverEmail.toLowerCase() === userEmail.toLowerCase()
            );
            if (isApprover) {
              console.log('✅ Matched as one of multiple approvers');
            }
            return isApprover;
          }
          
          console.log('❌ Not matched as approver');
          return false;
        });
        console.log('⏳ Pending approvals:', pendingForApproval);
        setPendingApprovals(pendingForApproval.length);
      }
    };
    
    checkApproverStatus();
    
    // Load notifications
    const userNotifications = getNotifications(userEmail);
    setNotifications(userNotifications.slice(0, 10)); // Show latest 10
    setUnreadCount(getUnreadCount(userEmail));
    
    // Cleanup old notifications
    cleanupOldNotifications();
  }, [account]);

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleFormClick = (formId) => {
    if (formId === 'extensions') {
      navigate('/extensions-request');
    } else if (formId === 'academic') {
      navigate('/academic-changes');
    } else if (formId === 'travel') {
      navigate('/travel-request');
    } else if (formId === 'leave') {
      navigate('/leave-request');
    } else if (formId === 'reimbursement') {
      navigate('/expense-reimbursement');
    } else if (formId === 'marketing') {
      navigate('/marketing-request');
    } else if (formId === 'wfh') {
      navigate('/wfh-checklist');
    } else if (formId === 'reports') {
      navigate('/reports');
    } else {
      toast.info(`${formId} form will be available soon!`);
    }
  };

  const handleMyRequests = () => {
    navigate('/my-requests');
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.id);
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Navigate to the request
    navigate(`/request/${notification.requestId}`);
    setShowNotifications(false);
  };

  const handleMarkAllAsRead = () => {
    const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
    markAllAsRead(userEmail);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<Description />}
            onClick={handleMyRequests}
            sx={{ mr: 2 }}
          >
            My Requests
          </Button>

          {isApprover && pendingApprovals > 0 && (
            <Button
              color="inherit"
              startIcon={
                <Badge badgeContent={pendingApprovals} color="error">
                  <Assignment />
                </Badge>
              }
              onClick={() => navigate('/my-requests?tab=approvals&status=pending')}
              sx={{ mr: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            >
              Pending Approvals
            </Button>
          )}

          <IconButton 
            color="inherit" 
            sx={{ mr: 2 }}
            onClick={(e) => setShowNotifications(!showNotifications)}
            aria-describedby="notifications-popover"
          >
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ff6b00' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Popover */}
      <Popover
        id="notifications-popover"
        open={showNotifications}
        anchorEl={document.querySelector('[aria-describedby="notifications-popover"]')}
        onClose={() => setShowNotifications(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: 400, maxHeight: 500 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f5f5' }}>
            <Typography variant="h6">Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllAsRead}>
                Mark all as read
              </Button>
            )}
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {notifications.map((notification) => (
                <React.Fragment key={notification.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.read ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notification.createdAt).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Welcome Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Welcome back, {account?.name?.split(' ')[0] || 'User'}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select a form below to submit a new request or click "My Requests" to view your submissions.
            </Typography>
          </Box>

          {/* Stats Bar */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              { label: 'Pending Requests', value: stats.pending, color: '#f57c00' },
              { label: 'Approved', value: stats.approved, color: '#388e3c' },
              { label: 'In Progress', value: stats.inProgress, color: '#0288d1' },
              { label: 'Total Submitted', value: stats.total, color: '#5e35b1' },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    borderTop: `4px solid ${stat.color}`,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Approver Section */}
          {isApprover && pendingApprovals > 0 && (
            <Box sx={{ mb: 4 }}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: '#fff3e0',
                  border: '2px solid #ff6b00',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  }
                }}
                onClick={() => navigate('/my-requests?tab=approvals&status=pending')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Badge badgeContent={pendingApprovals} color="error">
                      <Avatar sx={{ bgcolor: '#ff6b00', width: 56, height: 56 }}>
                        <Assignment fontSize="large" />
                      </Avatar>
                    </Badge>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        You have {pendingApprovals} pending approval{pendingApprovals > 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click here to review pending requests
                      </Typography>
                    </Box>
                  </Box>
                  <Button variant="contained" color="warning" endIcon={<Assignment />}>
                    Review Now
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Form Tiles */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Available Forms
          </Typography>
          
          <Grid container spacing={3}>
            {formTypes.map((form, index) => (
              <Grid item xs={12} sm={6} md={4} key={form.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ height: '100%' }}
                >
                  <Card sx={{ height: '100%', minHeight: 220, display: 'flex', flexDirection: 'column' }}>
                    <CardActionArea
                      onClick={() => handleFormClick(form.id)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 2,
                            bgcolor: form.bgColor,
                            color: form.color,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {React.cloneElement(form.icon, { fontSize: 'large' })}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {form.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                          {form.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Quick Actions */}
          <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Description />}
                  onClick={handleMyRequests}
                >
                  View My Requests
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Notifications />}
                  onClick={() => toast.info('Notifications coming soon!')}
                >
                  Notifications
                </Button>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;